import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, Plus } from "lucide-react";
import { api } from "../../lib/api";
import { frDate, frTime, RDV_CHANGED, TYPE_STYLE } from "../../lib/planning";
import RdvDialog from "./RdvDialog";

function Row({ r, onOpen, past }) {
  return (
    <li>
      <button onClick={() => onOpen(r)} className={`w-full flex items-center gap-3 px-2 py-2 rounded text-left hover:bg-norea-surfaceHover transition-colors ${past ? "opacity-60" : ""}`} data-testid={`dossier-rdv-${r.id}`}>
        <span className="w-1 self-stretch rounded" style={{ background: r.couleur }} />
        <span className="w-36 text-xs"><b className="capitalize">{frDate(r.start)}</b><br /><span className="text-norea-textMuted">{frTime(r.start)} – {frTime(r.end)}</span></span>
        <span className={`text-[11px] px-1.5 py-0.5 rounded border ${TYPE_STYLE[r.type]}`}>{r.type_label}{r.sav_reference ? ` · ${r.sav_reference}` : ""}</span>
        <span className="text-xs flex-1 truncate text-norea-textMuted">{r.techniciens.map((t) => t.nom).join(", ") || r.equipe?.nom || "intervenant à définir"}</span>
        <span className="text-[11px] text-norea-textMuted">{r.status_label}</span>
      </button>
    </li>
  );
}

export default function DossierAgendaCard({ dossier }) {
  const [items, setItems] = useState(null);
  const [dialog, setDialog] = useState(null);
  const load = useCallback(() => api.get("/planning/rdv", { params: { dossier_id: dossier.id } }).then((r) => setItems(r.data.items)).catch(() => setItems([])), [dossier.id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { window.addEventListener(RDV_CHANGED, load); return () => window.removeEventListener(RDV_CHANGED, load); }, [load]);
  const now = new Date().toISOString().slice(0, 16);
  const next = (items || []).filter((r) => r.end >= now);
  const past = (items || []).filter((r) => r.end < now).reverse();
  return (
    <section className="card-flat p-5" data-testid="dossier-agenda-card">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-heading text-base font-medium inline-flex items-center gap-2"><CalendarDays className="w-4 h-4 text-norea-primary" />Agenda du dossier</h3>
        <div className="flex items-center gap-2"><Link to="/planning" className="text-[11px] text-norea-textMuted hover:underline" data-testid="dossier-agenda-planning-link">Ouvrir le planning</Link>
          <button onClick={() => setDialog({ preset: { dossier_id: dossier.id } })} className="btn-secondary !py-1 text-xs" data-testid="dossier-agenda-new"><Plus className="w-3.5 h-3.5" />Nouveau rendez-vous</button></div>
      </div>
      {items === null ? <div className="h-12 animate-pulse bg-norea-bg rounded" /> : !items.length ? (
        <div className="py-4 text-center text-sm italic text-norea-textMuted" data-testid="dossier-agenda-empty">Aucun rendez-vous pour ce dossier.</div>
      ) : (
        <div className="space-y-2">
          <div className="overline">À venir ({next.length})</div>
          <ul data-testid="dossier-agenda-next">{next.map((r) => <Row key={r.id} r={r} onOpen={(x) => setDialog({ rdv: x })} />)}{!next.length && <li className="text-xs italic text-norea-textMuted px-2">Aucun.</li>}</ul>
          {past.length > 0 && <><div className="overline pt-1">Passés ({past.length})</div>
            <ul data-testid="dossier-agenda-past">{past.map((r) => <Row key={r.id} r={r} past onOpen={(x) => setDialog({ rdv: x })} />)}</ul></>}
        </div>
      )}
      {dialog && <RdvDialog rdv={dialog.rdv} preset={dialog.preset} onClose={() => setDialog(null)} onSaved={load} />}
    </section>
  );
}
